import React, { useEffect, useState, useRef } from 'react';
import api from '../services/api';
import { Bot, Plus, RefreshCw, Trash2, Power, MoreHorizontal, Edit2, Eye, Check, X, User, Copy } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { Toast } from '../Toast';
import { AddBotModal } from '../components/AddBotModal';
import { BotEditModal } from '../components/BotEditModal';
import { ConfirmModal } from '../ConfirmModal';

interface BotData {
    id: string; // UUID from Supabase
    name: string; // Nome de exibição (ex: "Meu Bot")
    username?: string; // Handle do Telegram (ex: "MeuBotBot") — usado no link t.me
    status: string; // online, offline, discovered
    bot_token?: string;
    session_string?: string; // Used to track owner
    welcome_message?: string;
    support_text?: string;
    welcome_media?: string;
}

/** Link para abrir o bot no Telegram. Usa username (handle), não o nome de exibição. */
function getBotTelegramLink(bot: BotData): string {
    const handle = (bot.username || bot.name || '').replace(/^@/, '').trim();
    if (!handle) return 'https://t.me/';
    return `https://t.me/${encodeURIComponent(handle)}`;
}

interface Account { id: number | string; phone: string; status?: string; is_active?: boolean; user_id?: string | null; }
export const Bots = ({ accountId, accounts = [], onSelectAccount }: { accountId: number | string | null; accounts?: Account[]; onSelectAccount?: (id: number | string) => void }) => {
    const [bots, setBots] = useState<BotData[]>([]);
    const [loading, setLoading] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingBot, setEditingBot] = useState<BotData | null>(null);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'info' | 'error' } | null>(null);

    // Menu State
    const [activeMenuBotId, setActiveMenuBotId] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Delete Confirmation State
    const [botToDelete, setBotToDelete] = useState<BotData | null>(null);

    useEffect(() => {
        hasDoneInitialScan.current = false;
        if (accountId) {
            fetchBots(0);
            const interval = setInterval(() => fetchBots(0), 60000);
            return () => {
                clearInterval(interval);
                refetchEmptyTimerRef.current.forEach(t => clearTimeout(t));
                refetchEmptyTimerRef.current = [];
            };
        } else {
            setBots([]);
            refetchEmptyTimerRef.current.forEach(t => clearTimeout(t));
            refetchEmptyTimerRef.current = [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId]);

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setToast({ msg: 'Link copiado!', type: 'success' });
        setActiveMenuBotId(null);
    };

    const hasDoneInitialScan = useRef(false);
    const refetchEmptyTimerRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const fetchBots = async (retryCount = 0): Promise<void> => {
        if (!accountId) return;
        const maxRetries = 3;
        const retryDelays = [800, 2000, 4000];

        if (bots.length === 0) setLoading(true);

        try {
            const res = await api.get('/bots', { params: { account_id: accountId } });
            const list = Array.isArray(res.data) ? res.data : [];
            setBots(list);

            if (list.length === 0 && !hasDoneInitialScan.current) {
                hasDoneInitialScan.current = true;
                scanBotsBackground();
                // Refetch após 2s e 5s para dar tempo do scan/backend — evita precisar de vários F5
                refetchEmptyTimerRef.current.forEach(t => clearTimeout(t));
                refetchEmptyTimerRef.current = [
                    setTimeout(() => fetchBots(0), 2000),
                    setTimeout(() => fetchBots(0), 5000),
                ];
            } else if (list.length > 0) {
                refetchEmptyTimerRef.current.forEach(t => clearTimeout(t));
                refetchEmptyTimerRef.current = [];
            }
        } catch (e: any) {
            console.error("Erro fetching bots", e);
            if (retryCount < maxRetries) {
                const delay = retryDelays[retryCount] ?? 4000;
                setTimeout(() => fetchBots(retryCount + 1), delay);
                return;
            }
        } finally {
            setLoading(false);
        }
    };

    const scanBotsBackground = async () => {
        if (!accountId) return;
        try {
            await api.post('/bots/scan', { account_id: accountId });
            fetchBots(0);
        } catch (e) {
            console.warn("Background scan warning:", e);
        }
    };

    const handleCreateSuccess = () => {
        fetchBots();
        scanBotsBackground(); // Force scan to find the new bot
        setToast({ msg: 'Bot adicionado com sucesso!', type: 'success' });
    };

    // --- ACTIONS ---

    const handleToggleStatus = async (bot: BotData) => {
        const newStatus = bot.status === 'online' ? 'offline' : 'online';
        try {
            await api.patch(`/bots/${bot.id}/status`, { status: newStatus });

            // Update local state immediately
            setBots(bots.map(b => b.id === bot.id ? { ...b, status: newStatus } : b));

            setToast({
                msg: `Bot ${newStatus === 'online' ? 'ativado' : 'desativado'} com sucesso!`,
                type: 'success'
            });
        } catch (e) {
            console.error(e);
            setToast({ msg: 'Erro ao alterar status.', type: 'error' });
        }
        setActiveMenuBotId(null);
    };

    const confirmDelete = (bot: BotData) => {
        setBotToDelete(bot);
        setActiveMenuBotId(null);
    };

    const handleDelete = async () => {
        if (!botToDelete) return;
        try {
            await api.delete(`/bots/${botToDelete.id}`);
            setBots(bots.filter(b => b.id !== botToDelete.id));
            setToast({ msg: 'Bot excluído com sucesso.', type: 'success' });
        } catch (e) {
            console.error(e);
            setToast({ msg: 'Erro ao excluir bot.', type: 'error' });
        } finally {
            setBotToDelete(null);
        }
    };

    const handleViewDetails = (bot: BotData) => {
        setToast({ msg: `Detalhes de ${bot.name}: Token: ${bot.bot_token?.substring(0, 10)}...`, type: 'info' });
        setActiveMenuBotId(null);
    };

    return (
        <div className="dashboard-view">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={!!botToDelete}
                title="Excluir Bot"
                message={`Tem certeza que deseja excluir ${botToDelete?.name}? Essa ação não pode ser desfeita.`}
                onConfirm={handleDelete}
                onCancel={() => setBotToDelete(null)}
            />

            {showAddModal && (
                <AddBotModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={handleCreateSuccess}
                    accountId={accountId}
                    accounts={accounts}
                    onSelectAccount={onSelectAccount}
                />
            )}

            {editingBot && (
                <BotEditModal
                    bot={editingBot}
                    onClose={() => setEditingBot(null)}
                    onSuccess={() => {
                        fetchBots();
                        setToast({ msg: "Bot atualizado com sucesso!", type: "success" });
                    }}
                />
            )}

            <div className="top-bar">
                <div>
                    <h1 className="page-title">Meus Bots</h1>

                </div>

                <div className="top-actions">
                    <GlassButton onClick={() => setShowAddModal(true)} style={{ width: 'auto' }}>
                        <Plus size={16} /> Novo Bot
                    </GlassButton>
                </div>
            </div>

            {!accountId ? null : bots.length === 0 ? (
                <div className="empty-state-pro">
                    <div className="empty-icon-box">
                        <Bot size={48} />
                    </div>
                    <h3>Nenhum bot encontrado</h3>
                    <p>Conecte uma conta Telegram no engine e aguarde a auto-descoberta.</p>
                </div>
            ) : (
                <div className="bots-grid main-grid">
                    {bots.map(bot => (
                        <div key={bot.id} className="bot-card-premium" style={{
                            background: '#111',
                            borderRadius: '16px',
                            border: '1px solid #333',
                            overflow: 'visible', // Changed to visible for menu
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}>
                            {/* HEADER */}
                            <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <h3 style={{ margin: 0, fontSize: '16px', color: '#fff', fontWeight: 600 }}>{bot.name}</h3>
                                    <Bot size={14} color="#666" />
                                </div>

                                {/* MENU TRIGGER */}
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveMenuBotId(activeMenuBotId === bot.id ? null : bot.id);
                                    }}
                                    style={{
                                        color: '#666',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'background 0.2s',
                                        background: activeMenuBotId === bot.id ? 'rgba(255,255,255,0.1)' : 'transparent'
                                    }}
                                >
                                    <MoreHorizontal size={20} />
                                </div>

                                {/* DROPDOWN MENU */}
                                {activeMenuBotId === bot.id && (
                                    <div ref={menuRef} className="bot-card-menu">
                                        <div
                                            onClick={() => {
                                                setEditingBot(bot);
                                                setActiveMenuBotId(null);
                                            }}
                                            style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: '#eee',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #282828',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#282828'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <Edit2 size={16} /> Editar Bot
                                        </div>

                                        <div
                                            onClick={() => handleViewDetails(bot)}
                                            style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: '#eee',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #282828',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#282828'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <Eye size={16} /> Ver Detalhes
                                        </div>

                                        <div
                                            onClick={() => handleToggleStatus(bot)}
                                            style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: bot.status === 'online' ? '#ef4444' : '#22c55e',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #282828',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#282828'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <Power size={16} /> {bot.status === 'online' ? 'Desativar' : 'Ativar'}
                                        </div>

                                        <div
                                            onClick={() => confirmDelete(bot)}
                                            style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: '#ef4444',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <Trash2 size={16} /> Excluir bot
                                        </div>

                                        <div
                                            onClick={() => handleCopy(getBotTelegramLink(bot))}
                                            style={{
                                                padding: '12px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                color: '#fff',
                                                fontSize: '14px',
                                                cursor: 'pointer',
                                                borderTop: '1px solid #282828',
                                                background: '#222'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = '#222'}
                                        >
                                            <Copy size={16} /> Copiar link
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* BODY INFO */}
                            <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                {/* Row: Conta */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #222', paddingBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#999', fontSize: '14px' }}>
                                        <div style={{ width: '20px' }}><User size={16} /></div>
                                        <span>Conta Responsável</span>
                                    </div>
                                    <div style={{ color: '#fff', fontSize: '14px', fontFamily: 'monospace' }}>
                                        {bot.session_string ? bot.session_string.split(':')[0] : 'Desconhecida'}
                                    </div>
                                </div>

                                {/* Row: Telegram Link */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#999', fontSize: '14px' }}>
                                        <div style={{ width: '20px' }}><RefreshCw size={16} /></div>
                                        <span>Link do Bot</span>
                                    </div>
                                    <button
                                        className="copy-btn-link"
                                        onClick={() => handleCopy(getBotTelegramLink(bot))}
                                        style={{ background: 'transparent', border: '1px solid #333', padding: '6px 12px', borderRadius: '6px', color: '#fff', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                    >
                                        <Bot size={12} /> Copiar
                                    </button>
                                </div>

                            </div>

                            {/* FOOTER STATUS */}
                            <div style={{
                                marginTop: 'auto',
                                background: bot.status === 'online' ? 'rgba(34, 197, 94, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                                padding: '20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderTop: bot.status === 'online' ? '1px solid rgba(34, 197, 94, 0.1)' : '1px solid rgba(239, 68, 68, 0.1)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '32px', height: '32px', borderRadius: '50%',
                                        background: bot.status === 'online' ? '#22c55e' : bot.status === 'discovered' ? '#eab308' : '#ef4444',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#000'
                                    }}>
                                        {bot.status === 'online' ? <Check size={16} /> : bot.status === 'discovered' ? <RefreshCw size={16} /> : <X size={16} />}
                                    </div>
                                    <div>
                                        <div style={{ color: '#fff', fontSize: '14px', fontWeight: 500 }}>
                                            Este bot está: <span style={{
                                                background: bot.status === 'online' ? '#22c55e' : bot.status === 'discovered' ? '#eab308' : '#333',
                                                color: (bot.status === 'online' || bot.status === 'discovered') ? '#000' : '#888',
                                                padding: '2px 8px', borderRadius: '4px', fontSize: '12px', marginLeft: '6px'
                                            }}>{bot.status === 'online' ? 'Ativo' : bot.status === 'discovered' ? 'Pendente' : 'Inativo'}</span>
                                        </div>
                                        <div style={{ color: '#666', fontSize: '11px', marginTop: '2px' }}>
                                            {bot.status === 'online' ? 'Bot rodando normalmente' : bot.status === 'discovered' ? 'Aguardando token via BotFather' : 'Clique para ativar o bot'}
                                        </div>
                                    </div>
                                </div>

                                {/* Toggle Switch Mockup */}
                                <div
                                    onClick={() => handleToggleStatus(bot)}
                                    style={{
                                        width: '44px', height: '24px', borderRadius: '12px',
                                        background: bot.status === 'online' ? '#fff' : '#333',
                                        position: 'relative', cursor: 'pointer',
                                        opacity: 1
                                    }}>
                                    <div style={{
                                        width: '20px', height: '20px', borderRadius: '50%', background: '#000',
                                        position: 'absolute', top: '2px', left: bot.status === 'online' ? '22px' : '2px',
                                        transition: 'all 0.3s'
                                    }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
